import React, { useEffect, useMemo, useState } from 'react';
import {
  useListenerAudioByListenerId,
  ListenerAudio,
  EXPERIENCE_GRADE,
  getPipelineStatusLabel,
} from '../../../../hooks/useListenerAudio';
import ExperienceDetailView from './experience-detail-view';
import Spinner from '../../../../components/common/spinner';
import { useQueryClient } from '@tanstack/react-query';

interface ExperienceAccordionSectionProps {
  sectionId: string;
  title: string;
  experienceType: 'audio' | 'video';
  grade: 'ungraded' | 'approved' | 'rejected';
  isOpen: boolean;
  toggleSection: (sectionId: string) => void;
  listenerRoleId: number;
  refetchPeerData: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const ExperienceAccordionSection: React.FC<ExperienceAccordionSectionProps> = ({
  sectionId,
  title,
  experienceType,
  grade,
  isOpen,
  toggleSection,
  listenerRoleId,
  refetchPeerData,
  onDirtyChange,
}) => {
  const [selectedExperienceId, setSelectedExperienceId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const {
    data: experiences,
    isLoading,
    refetch,
  } = useListenerAudioByListenerId(listenerRoleId, {
    page: 1,
    limit: 100,
    search: '',
    grade: grade,
    experience_type: experienceType,
    order_by: 'created_at',
    order_by_direction: 'desc',
  });

  const targetQuality = EXPERIENCE_GRADE[grade];

  const experiencesList = useMemo(
    () => (experiences?.data || []).filter((e) => e.message_quality === targetQuality),
    [experiences?.data, targetQuality],
  );
  const experienceCount = experiencesList.length;

  const selectedExperience = experiencesList.find((exp) => exp.id === selectedExperienceId);

  // Auto-select first experience when section opens
  useEffect(() => {
    if (isOpen && experiencesList.length > 0 && !selectedExperienceId) {
      setSelectedExperienceId(experiencesList[0].id);
    }
  }, [isOpen, experiencesList, selectedExperienceId]);

  const countText = useMemo(() => {
    if (grade === 'ungraded') {
      return `${experienceCount} to approve`;
    } else if (grade === 'approved') {
      return `${experienceCount} of ${experienceCount} Experiences approved`;
    } else {
      return `${experienceCount} Rejected Experience${experienceCount !== 1 ? 's' : ''}`;
    }
  }, [experienceCount, grade]);

  const summaryNeedsAttention = !isOpen && experienceCount > 0 && (grade === 'ungraded' || grade === 'rejected');

  const getStatusBadge = (experience: ListenerAudio) => {
    if (experience.message_quality === EXPERIENCE_GRADE.approved) {
      return (
        <span
          className="rounded px-2 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: '#dcfce7', color: '#166534' }}
        >
          Approved
        </span>
      );
    }
    if (experience.message_quality === EXPERIENCE_GRADE.rejected) {
      return (
        <span
          className="rounded px-2 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
        >
          Rejected
        </span>
      );
    }
    return (
      <span
        className="rounded px-2 py-0.5 text-xs font-semibold"
        style={{ backgroundColor: '#ffedd5', color: '#9a3412' }}
      >
        Pending
      </span>
    );
  };

  return (
    <div className="rounded-lg border border-gray-300 bg-white">
      <button
        className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
        onClick={() => toggleSection(sectionId)}
      >
        <h4 className="text-base font-semibold text-gray-900">{title}</h4>
        <div className="flex items-center gap-3">
          <span className={summaryNeedsAttention ? 'text-red text-sm font-semibold' : 'text-sm text-gray-600'}>
            {countText}
          </span>
          <svg
            className={`h-5 w-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-gray-200">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : experienceCount === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No {title.toLowerCase()} found.</div>
          ) : (
            <div className="h-[600px] overflow-y-auto">
              <div className="flex min-h-full">
                {/* Left: Experience List (sticky; no separate scrollbar) */}
                <div className="sticky top-0 h-fit w-64 shrink-0 border-r border-gray-200 bg-gray-50">
                  {experiencesList.map((experience: ListenerAudio) => (
                    <button
                      key={experience.id}
                      onClick={() => setSelectedExperienceId(experience.id)}
                      className={`w-full border-b border-gray-200 p-3 text-left hover:bg-gray-100 ${
                        selectedExperienceId === experience.id ? 'border-l-4 border-l-blue-600 bg-blue-50' : ''
                      }`}
                    >
                      <p className="mb-1 text-sm font-semibold text-gray-900">
                        {experience.peer_title || experience.title || experience.ai_title || 'Untitled'}
                      </p>
                      <p className="mb-1 text-xs text-gray-500">#{experience.id}</p>
                      <p className="mb-2 text-xs text-gray-600">Pipeline: {getPipelineStatusLabel(experience)}</p>
                      {experience.experience_tag && (
                        <span className="mb-2 inline-block rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                          {experience.experience_tag.name}
                        </span>
                      )}
                      <div className="mt-2">{getStatusBadge(experience)}</div>
                    </button>
                  ))}
                </div>

                {/* Right: Experience Details */}
                <div className="min-w-0 flex-1 px-4 pb-4">
                  {selectedExperience ? (
                    <ExperienceDetailView
                      key={selectedExperience.id}
                      experience={selectedExperience}
                      experienceType={experienceType}
                      refetchExperiences={refetch}
                      onDirtyChange={onDirtyChange}
                      onGraded={() => {
                        queryClient.invalidateQueries(['listener_audio']);
                        queryClient.invalidateQueries(['listener_audio_browse']);
                        setSelectedExperienceId(null);
                      }}
                      onUploaded={async () => {
                        await queryClient.invalidateQueries(['listener_audio']);
                        await queryClient.invalidateQueries(['listener_audio_browse']);
                        setSelectedExperienceId(null);
                        setTimeout(() => setSelectedExperienceId(selectedExperience.id), 50);
                      }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-gray-500">
                      Select an experience to view details
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExperienceAccordionSection;
